import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import api, { formatApiErrorDetail } from "../../api";
import { localISODate } from "./dates";
import { STATE_LABEL } from "./labels";

const DAY_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

// The Monday-first grid covering a whole month, always six rows so the layout
// does not jump between months.
function monthGrid(year, month) {
  const first = new Date(year, month, 1);
  const offset = (first.getDay() + 6) % 7;      // JS weeks start on Sunday
  const start = new Date(year, month, 1 - offset);
  return Array.from({ length: 42 }, (_, i) => {
    const d = new Date(start.getFullYear(), start.getMonth(), start.getDate() + i);
    return { iso: localISODate(d), day: d.getDate(), inMonth: d.getMonth() === month };
  });
}

export default function QueueCalendar({ onOpen, onChanged, reloadKey }) {
  const today = useMemo(() => new Date(), []);
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth());
  const [scheduled, setScheduled] = useState([]);
  const [unscheduled, setUnscheduled] = useState([]);
  const [dragging, setDragging] = useState(null);
  const [over, setOver] = useState(null);
  const [error, setError] = useState("");
  // Synchronous re-entrancy guard for `setDate`, same reasoning as
  // Queue.jsx's `moveInFlight` and ItemDrawer's `inFlight`: a drop handler
  // can fire twice, and a state flag alone isn't synchronous enough to
  // catch a second call landing before the first await resolves. This is a
  // PATCH, not a transition, so there's no trail entry to duplicate — but a
  // duplicate PATCH is still a duplicate network write worth closing off.
  const patchInFlight = useRef(false);

  const cells = useMemo(() => monthGrid(year, month), [year, month]);
  const from = cells[0].iso;
  const to = cells[cells.length - 1].iso;

  const load = useCallback(() => {
    return api.get("/marketing/calendar", { params: { from, to } })
      .then(({ data }) => { setScheduled(data.scheduled); setUnscheduled(data.unscheduled); setError(""); })
      .catch((e) => setError(formatApiErrorDetail(e?.response?.data?.detail)));
  }, [from, to]);

  useEffect(() => { load(); }, [load, reloadKey]);

  const setDate = async (id, planned_for) => {
    if (patchInFlight.current) return;
    patchInFlight.current = true;
    try {
      await api.patch(`/marketing/items/${id}`, { planned_for });
      await load();
      await onChanged();
    } catch (e) {
      setError(formatApiErrorDetail(e?.response?.data?.detail));
    } finally {
      patchInFlight.current = false;
    }
  };

  const step = (delta) => {
    const d = new Date(year, month + delta, 1);
    setYear(d.getFullYear());
    setMonth(d.getMonth());
  };

  // Native HTML5 drag-and-drop is mouse-only. Chips are also click- and
  // keyboard-openable (Enter/Space), matching the board's cardlets and the
  // house pattern from PlaybookCard.
  const openKey = (id) => (e) => {
    if (e.key === "Enter") {
      onOpen(id);
    } else if (e.key === " " || e.key === "Spacebar") {
      e.preventDefault();
      onOpen(id);
    }
  };

  const chip = (it) => (
    <div key={it.id} className={`mk-chipcard state-${it.state}`} draggable
      onDragStart={(e) => { setDragging(it); e.dataTransfer.effectAllowed = "move"; e.dataTransfer.setData("text/plain", it.id); }}
      onDragEnd={() => { setDragging(null); setOver(null); }}
      onClick={() => onOpen(it.id)}
      role="button"
      tabIndex={0}
      onKeyDown={openKey(it.id)}
      title={`${STATE_LABEL[it.state]} — ${it.title}`}
      data-testid={`mk-chipcard-${it.id}`}>
      {it.title}
    </div>
  );

  return (
    <div className="mk-calwrap" data-testid="mk-calendar">
      <div className="mk-cal">
        <div className="mk-cal-head">
          <button type="button" className="mk-btn-quiet" onClick={() => step(-1)}>←</button>
          <span className="mk-cal-month">
            {new Date(year, month, 1).toLocaleString(undefined, { month: "long", year: "numeric" })}
          </span>
          <button type="button" className="mk-btn-quiet" onClick={() => step(1)}>→</button>
        </div>
        {error && <div className="mk-error">{error}</div>}
        <div className="mk-cal-grid">
          {DAY_LABELS.map((d) => <div key={d} className="mk-cal-daylabel">{d}</div>)}
          {cells.map((c) => (
            <div key={c.iso}
              className={`mk-cal-cell ${c.inMonth ? "" : "out"} ${over === c.iso ? "over" : ""}`}
              onDragOver={(e) => { if (dragging) { e.preventDefault(); setOver(c.iso); } }}
              onDragLeave={() => setOver(null)}
              onDrop={(e) => {
                e.preventDefault();
                setOver(null);
                const it = dragging;
                setDragging(null);
                if (it && it.planned_for !== c.iso) setDate(it.id, c.iso);
              }}
              data-testid={`mk-cal-${c.iso}`}>
              <div className="mk-cal-daynum">{c.day}</div>
              {scheduled.filter((i) => i.planned_for === c.iso).map(chip)}
            </div>
          ))}
        </div>
      </div>

      <aside className="mk-rail" data-testid="mk-cal-rail"
        onDragOver={(e) => { if (dragging) e.preventDefault(); }}
        onDrop={(e) => {
          e.preventDefault();
          const it = dragging;
          setDragging(null);
          if (it && it.planned_for) setDate(it.id, null);
        }}>
        <div className="mk-label">Unscheduled</div>
        <div className="mk-rail-note">
          Drag onto a date to say when we mean to publish it. Drag back here to unschedule.
        </div>
        {unscheduled.map(chip)}
        {!unscheduled.length && <div className="mk-col-empty">Nothing waiting.</div>}
      </aside>
    </div>
  );
}
